import { Box, Button, Form } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import SelectField from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import app from 'modules/core/store/models/app'

import {
  CategoryPolicyRule,
  parseAffectees,
  useCreateCategoryPolicyRule,
  useUpdateCategoryPolicyRule
} from 'modules/expenses/hooks/useCategoryPolicyRules'

import AffecteesSelector, { AffecteeEntry } from './AffecteesSelector'

const FREQUENCY_OPTIONS = [
  { value: 'per_expense', label: 'Per expense' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
]

interface FormValues {
  frequency: string
  limitAmountCents: number | null | undefined
  affectees: AffecteeEntry[]
}

interface Props {
  categoryId: number
  rule?: CategoryPolicyRule
  onClose: () => void
}

const RuleFormModal = ({ categoryId, rule, onClose }: Props) => {
  const { company } = useCurrentSession()
  const { create } = useCreateCategoryPolicyRule()
  const { update } = useUpdateCategoryPolicyRule()
  const isEditing = !!rule

  const form = useForm<FormValues>({
    validateOnChange: false,
    defaultValues: {
      frequency: rule?.frequency || '',
      limitAmountCents: rule?.limitAmountCents ? fromCents(rule.limitAmountCents) : null,
      affectees: parseAffectees(rule?.peopleGroupAffectees) ?? []
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}
      if (!data.frequency) {
        errors.frequency = 'Required'
      }
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const limitAmountCents = data.limitAmountCents ? toCents(Number(data.limitAmountCents)) : null
      const peopleGroupAffectees = data.affectees.length > 0 ? data.affectees : null

      if (isEditing) {
        const result = await update({
          id: rule.id,
          frequency: data.frequency,
          limitAmountCents,
          peopleGroupAffectees
        })
        const handled = handleResult(result, (errors) => setErrors(errors))
        if (handled.success) {
          app.setFlash({ type: 'correct', message: 'Saved successfully' })
          onClose()
        } else {
          app.setFlash({ type: 'wrong', message: 'Error saving' })
        }
        return handled
      } else {
        const result = await create({
          companyId: company.id,
          financeCustomCategoryId: categoryId,
          frequency: data.frequency,
          limitAmountCents,
          peopleGroupAffectees
        })
        const handled = handleResult(result, (errors) => setErrors(errors))
        if (handled.success) {
          app.setFlash({ type: 'correct', message: 'Saved successfully' })
          onClose()
        } else {
          app.setFlash({ type: 'wrong', message: 'Error saving' })
        }
        return handled
      }
    }
  })

  return (
    <Modal
      illustration="document-edit"
      title={isEditing ? 'Edit rule' : 'Add rule'}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => form.handleSubmit()} disabled={form.isSubmitting}>
            Save
          </Button>
        </>
      }
    >
      <Form padding="s0">
        <SelectField
          mandatory
          label="Frequency"
          value={form.values.frequency}
          onChange={(value) => form.setFieldValue('frequency', value, false)}
          options={FREQUENCY_OPTIONS}
          error={form.errors.frequency}
          none="Select..."
        />

        <NumberField
          placeholder="0"
          label="Limit amount"
          value={form.values.limitAmountCents}
          error={form.errors.limitAmountCents}
          onChange={(value: number | null | undefined) =>
            form.setFieldValue('limitAmountCents', value, false)
          }
        />

        <Box flexDirection="column" gap="s8">
          <AffecteesSelector
            value={form.values.affectees}
            onChange={(entries) => form.setFieldValue('affectees', entries, false)}
          />
        </Box>
      </Form>
    </Modal>
  )
}

export default RuleFormModal
